import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { itToast as toast } from "../../utils/infinityToast";
import { Button } from "@/components/ui/button";
import { getImageUrl } from "../../utils/imageUtils";
import { useInfinityCustomer } from "../../context/InfinityCustomerContext";
import InfinityOrderList from "./InfinityOrderList";

const PRINT_LABEL = { fingerprint: "Fingerprint", handfoot: "Hand & Foot", nose: "Nose", paw: "Paw", signature: "Signature", picture: "Photo" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

/**
 * Home (TR-8 2.1) — the case is the landing page: case details first, then an
 * "Orders" section (#orders anchor) listing every order on the case. The nav's
 * "Orders" link scrolls here.
 */
export default function InfinityHome() {
  const { http, isAuthed, setStore, basePath, theme } = useInfinityCustomer();
  const navigate = useNavigate();
  const location = useLocation();
  const primary = theme?.primary_color || "#2E72BC";
  const ordersRef = useRef(null);

  const [caseData, setCaseData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingCase, setLoadingCase] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);

  useEffect(() => {
    if (!isAuthed) { navigate(`${basePath}/login`); return; }
    http.get("/case")
      .then((r) => { setCaseData(r.data); if (r.data.store) setStore(r.data.store); })
      .catch(() => toast.error("Could not load the case"))
      .finally(() => setLoadingCase(false));
    http.get("/orders")
      .then((r) => setOrders(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoadingOrders(false));
  }, []);

  // Scroll to the orders section (#orders) or a specific just-placed order
  // (#order-<id>, e.g. after checkout) once orders have loaded.
  useEffect(() => {
    if (!location.hash) return;
    const target = location.hash === "#orders"
      ? ordersRef.current
      : document.getElementById(location.hash.slice(1));
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, loadingOrders]);

  if (loadingCase) return <div className="flex justify-center py-16"><Loader2 className="w-10 h-10 animate-spin" style={{ color: primary }} /></div>;
  if (!caseData) return null;

  const s = caseData.subject || {};
  const row = (label, value) => (
    <div className="flex gap-2 text-sm py-0.5">
      <span className="text-slate-500 min-w-[140px]">{label}</span>
      <span className="font-medium text-slate-800">{value || "—"}</span>
    </div>
  );

  return (
    <div className="space-y-10">
      {/* Case details */}
      <section className="space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900">Case Details</h2>
          <Button onClick={() => navigate(`${basePath}/shop`)}>Select Fingerprint Jewelry</Button>
        </div>

        {caseData.captures?.length > 0 ? (
          <>
            <div className="flex flex-wrap gap-3">
              {caseData.captures.map((c) => (
                <a key={c.capture_id} href={getImageUrl(c.image_url)} target="_blank" rel="noreferrer"
                  className="block w-28 h-28 rounded-xl overflow-hidden border bg-slate-50" title="Click to download">
                  <img src={getImageUrl(c.image_url)} alt="Print" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
            <p className="text-xs text-slate-400 -mt-2">Click an image to view/download.</p>
          </>
        ) : null}

        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 card-elevated p-5">
          {row("Case Number", caseData.case_number)}
          {row("Print Type", PRINT_LABEL[caseData.print_type] || caseData.print_type)}
          {row("First Name", s.first_name)}
          {row("Middle Name", s.middle_name)}
          {row("Last Name", s.last_name)}
          {row("Gender", s.gender)}
          {row("Date of Birth", fmt(s.date_of_birth))}
          {row("Date of Death", fmt(s.date_of_death))}
          {row("Time of Death", s.time_of_death)}
          {row("Place of Death", s.place_of_death)}
          {row("Notes", caseData.notes)}
        </div>

        {caseData.funeral_home ? (
          <div className="text-xs text-slate-500">
            Arranged by <span className="font-semibold text-slate-700">{caseData.funeral_home.business_name}</span>
          </div>
        ) : null}
      </section>

      {/* Orders on this case */}
      <section ref={ordersRef} id="orders" className="space-y-4 scroll-mt-24">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900">Orders</h2>
          <Button variant="outline" onClick={() => navigate(`${basePath}/shop`)}>Place New Order</Button>
        </div>
        <InfinityOrderList orders={orders} loading={loadingOrders} />
      </section>
    </div>
  );
}
